import React, { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Pagination, Select, useMediaQuery } from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Btn, FlexDiv } from "../../assets/styles/style";
import { Base_URL } from "../../Client/apiURL";
import { GetQuestionTypeTests } from "../../Client/request";
import CircularLoader from "../../components/Login/CircularLoader";
import LoadingModal from "../../components/Common/LoadingModal";
import { PurpleBtn, ResetPurpleBtn } from "../../components/Common/Style";
import { OptionText } from "../../components/StrategyVideos/Style";
import { paginateQuestionTypeTests } from "./questionTypeMockListUtils";
import { buildMockTestPath } from '../../utils/mockTestLinks';
import MockTestShareButton from '../../components/Common/MockTestShareButton';

const categories = ["All", "Speaking", "Writing", "Reading", "Listening"];

const questionBadgeStyle = (compact) => ({
  borderRadius: "4px",
  backgroundColor: "rgba(253, 60, 101, 0.10)",
  padding: "7px 10px",
  fontSize: compact ? "12px" : "16px",
  color: "#fd3c65",
  margin: 0,
});

const timeBadgeStyle = (compact) => ({
  borderRadius: "4px",
  backgroundColor: "rgba(153, 108, 254, 0.10)",
  padding: "7px 10px",
  fontSize: compact ? "12px" : "16px",
  color: "#996cfe",
  margin: 0,
});

const QuestionTypeMockTests = ({ isPteCore, isSubscribed }) => {
  const navigate = useNavigate();
  const isCompact = useMediaQuery("(max-width:900px)");
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resettingId, setResettingId] = useState(null);
  const [category, setCategory] = useState("All");
  const [questionType, setQuestionType] = useState("All");
  const [page, setPage] = useState(1);

  const loadTests = async () => {
    setLoading(true);
    const result = await GetQuestionTypeTests(isPteCore);
    setTests(result.data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadTests();
  }, [isPteCore]);

  const availableTypes = useMemo(
    () => Array.from(new Set(
      tests
        .filter((test) => category === "All" || test.Category === category)
        .map((test) => test.QuestionTypeName)
    )).sort(),
    [tests, category]
  );

  useEffect(() => {
    if (questionType !== "All" && !availableTypes.includes(questionType)) {
      setQuestionType("All");
    }
  }, [availableTypes, questionType]);

  const visibleTests = useMemo(
    () => tests.filter((test) =>
      (category === "All" || test.Category === category) &&
      (questionType === "All" || test.QuestionTypeName === questionType)
    ),
    [tests, category, questionType]
  );

  const pagination = useMemo(
    () => paginateQuestionTypeTests(visibleTests, page),
    [visibleTests, page]
  );

  useEffect(() => {
    if (page !== pagination.page) setPage(pagination.page);
  }, [page, pagination.page]);

  useEffect(() => {
    setPage(1);
  }, [category, questionType, isPteCore]);

  const resetAttempt = async (attemptId) => {
    setResettingId(attemptId);
    try {
      await axios.delete(
        `${Base_URL}/app/users/mock-test-attempts/delete?mock_test_attempted_id=${attemptId}`,
        { withCredentials: true }
      );
      await loadTests();
    } finally {
      setResettingId(null);
    }
  };

  const openPlans = () => navigate("/plan-info");
  const openTest = (test) => navigate(buildMockTestPath({
    exam: 'pte',
    scope: 'question-type',
    id: test.MockTestId,
    name: test.MockTestName,
  }));

  const renderActionButtons = (test) => (
    <FlexDiv style={{ gap: "10px", alignItems: "center" }}>
      <MockTestShareButton path={buildMockTestPath({
        exam: 'pte',
        scope: 'question-type',
        id: test.MockTestId,
        name: test.MockTestName,
      })} />
      {isSubscribed && test.ResumeTestId && (
        <ResetPurpleBtn onClick={() => resetAttempt(test.ResumeTestId)}>
          {resettingId === test.ResumeTestId
            ? <CircularLoader color="white" />
            : "Reset"}
        </ResetPurpleBtn>
      )}
      {isSubscribed ? (
        <PurpleBtn onClick={() => openTest(test)}>
          {test.ResumeTestId ? "Resume" : "Start"}
        </PurpleBtn>
      ) : (
        <PurpleBtn
          onClick={openPlans}
          style={{ backgroundColor: "#c6cbd9" }}
        >
          Upgrade
        </PurpleBtn>
      )}
    </FlexDiv>
  );

  return (
    <Box sx={{ mt: 2 }}>
      <FlexDiv
        style={{
          justifyContent: "space-between",
          width: "100%",
          marginBottom: "1.63rem",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <FlexDiv style={{ gap: isCompact ? "1rem" : "2rem", flexWrap: "wrap" }}>
          {categories.map((item) => (
            <Btn
              key={item}
              onClick={() => {
                setCategory(item);
                setQuestionType("All");
              }}
            >
              <OptionText
                style={{
                  color: item === category ? "var(--Brand-Purple, #996CFE)" : "",
                  borderBottom: item === category
                    ? "1px solid var(--Brand-Purple, #996CFE)"
                    : "",
                }}
              >
                {item}
              </OptionText>
            </Btn>
          ))}
        </FlexDiv>
        <Select
          size="small"
          value={questionType}
          onChange={(event) => setQuestionType(event.target.value)}
          sx={{ minWidth: { xs: "100%", sm: 260 }, bgcolor: "white", borderRadius: 1 }}
        >
          <MenuItem value="All">All question types</MenuItem>
          {availableTypes.map((name) => (
            <MenuItem key={name} value={name}>{name}</MenuItem>
          ))}
        </Select>
      </FlexDiv>

      {loading ? (
        <LoadingModal open={loading} />
      ) : visibleTests.length === 0 ? (
        <FlexDiv>
          <div
            style={{
              textAlign: "center",
              fontSize: "20px",
              color: "#999",
              padding: "20px",
              fontWeight: "500",
            }}
          >
            No <span style={{ color: "rgb(171 144 233)" }}>
              {isPteCore ? "Core" : "Academic"}
            </span>{" "}
            Mock Test Available.
          </div>
        </FlexDiv>
      ) : (
        <div>
          {pagination.items.map((test) => (
            !isCompact ? (
              <div
                key={test.MockTestId}
                style={{
                  marginTop: ".1rem",
                  display: "flex",
                  justifyContent: "space-between",
                  backgroundColor: "white",
                  boxShadow: "0px 4px 10px 0px rgba(51, 51, 51, 0.08)",
                  padding: "0.5rem 1rem",
                }}
              >
                <FlexDiv style={{ gap: "15px", alignItems: "center" }}>
                  <p>{test.MockTestName}</p>
                  <p style={questionBadgeStyle(false)}>Question: {test.TotalQuestions}</p>
                  <p style={timeBadgeStyle(false)}>Approx Time: {Math.max(1, Number(test.TotalTime) || 1)} Minutes</p>
                </FlexDiv>
                {renderActionButtons(test)}
              </div>
            ) : (
              <div
                key={test.MockTestId}
                style={{
                  marginTop: ".1rem",
                  backgroundColor: "white",
                  boxShadow: "0px 4px 10px 0px rgba(51, 51, 51, 0.08)",
                  padding: "0.5rem 1rem",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: "15px", alignItems: "center" }}>
                  <p>{test.MockTestName}</p>
                </div>
                <FlexDiv style={{ justifyContent: "space-evenly", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                  <p style={questionBadgeStyle(true)}>Question: {test.TotalQuestions}</p>
                  <p style={timeBadgeStyle(true)}>Approx Time: {Math.max(1, Number(test.TotalTime) || 1)} Minutes</p>
                  {renderActionButtons(test)}
                </FlexDiv>
              </div>
            )
          ))}
        </div>
      )}

      {pagination.pageCount > 1 && (
        <div
          style={{
            marginTop: "10px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Pagination
            count={pagination.pageCount}
            page={pagination.page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </div>
      )}
    </Box>
  );
};

export default QuestionTypeMockTests;
